import moment from 'moment';

import { RelevanceIndicator } from './relevance-indicator';
import { Button } from '../shadcn/button';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import DateInputPopup from '@/components/molecules/DateInputPopup';
import EditableField from '@/components/molecules/EditableField';
import { ThemesTooltip } from '@/components/organisms/ThemesTooltip';
import { useDocumentNavigation } from '@/hooks/useOpenDocViewer';
import { useOpenInconsistencyViewer } from '@/hooks/useOpenInconsistencyViewer';
import { Chip } from '@/new/components/chip';
import { Icon } from '@/new/components/icon';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { TooltipIcon } from '@/new/components/tooltip-icon';
import { ChronologyFact, Fact, Relevance } from '@/types';

interface FactTitleProps {
  open: boolean;
  fact: Fact | ChronologyFact;
  factText: string;
  dateText: string;
  relevance: Relevance;
  updateFactDateText: (newDateText: string) => void;
  updateFactDescription: (newDescription: string) => void;
  isUpdatingDateText: boolean;
  isUpdatingDescription: boolean;
  reference?: number;
  hideRelevanceIndicator?: boolean;
  onViewDocument?: (docId: string, factId: string, pageNumber: number) => void;
  verification?: {
    verified: boolean;
    handleToggleVerify: (verify: boolean) => void;
    email?: string;
    date?: Date;
  };
}

export const FactTitle = ({
  open,
  fact,
  factText,
  dateText,
  relevance,
  reference,
  hideRelevanceIndicator,
  onViewDocument,
  updateFactDateText,
  updateFactDescription,
  isUpdatingDateText,
  isUpdatingDescription,
  verification,
}: FactTitleProps) => {
  const { openDocViewer } = useDocumentNavigation();
  const { openInconsistencyViewer } = useOpenInconsistencyViewer();

  const isFullFact = 'dateIsAmbiguous' in fact;

  return (
    <div className="flex w-full items-center justify-between font-normal">
      <div className="w-full p-2.5">
        <div className="flex w-full justify-between">
          <div className={`flex items-center gap-1.75 font-semibold`}>
            {isFullFact && fact.dateIsAmbiguous && (
              <TooltipIcon tooltipContent={fact.dateIsAmbiguousText || 'Date format is ambiguous, please check'} />
            )}

            {dateText ? dateText : '(Date Unknown)'}

            <DateInputPopup dateText={dateText} onUpdateDate={updateFactDateText} isLoading={isUpdatingDateText} />
          </div>

          <div className="flex items-center gap-2">
            <div className="flex gap-1 text-xs">
              {(isFullFact ? fact : fact.fact).locations.map((location, index) => {
                if (location.doc === undefined || location.doc === null) return <></>;
                if (index === 2) return <div key={`${index}-ellipsis`}>...</div>;
                if (index > 2) return <div key={`${index}-ellipsis`}></div>;

                const doc = location.doc;
                return (
                  <span key={`${doc.id}-${index}`} className="flex items-center justify-between gap-1">
                    <span
                      className="text-blue-600"
                      onClick={(e) => {
                        e.stopPropagation();

                        trackCustomEvent(TrackingEvents.OpenDocViewerFromFact);

                        (onViewDocument ?? openDocViewer)(
                          location.docId,
                          isFullFact ? fact.id : fact.fact.id,
                          location.boundingBoxes?.[0]?.pageNumber ?? 0,
                        );
                      }}
                    >
                      <TooltipProvider>
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger asChild>
                            <p className="line-clamp-1 max-w-28 break-all text-ellipsis">{doc.file.name}</p>
                          </TooltipTrigger>

                          <TooltipContent className="max-w-56 bg-gray-50">
                            <p className="text-xs text-gray-800">{doc.file.name}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </span>
                    {doc.type && <Chip className="bg-brandTertiary/10 text-brandTertiary">{doc.type}</Chip>}
                    {(isFullFact ? fact : fact.fact).locations.length > 1 &&
                    index < (isFullFact ? fact : fact.fact).locations.length - 1 ? (
                      <span className="text-gray-400">/</span>
                    ) : (
                      <></>
                    )}
                  </span>
                );
              })}
            </div>

            {isFullFact && fact.inconsistencies && fact.inconsistencies.length ? (
              <Chip className="bg-red-50 hover:bg-red-100">
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-0 text-xs text-red-500 hover:text-red-600"
                  onClick={() => {
                    trackCustomEvent(TrackingEvents.OpenInconsistencyViewer);
                    fact.inconsistencies &&
                      openInconsistencyViewer(
                        fact.inconsistencies.map((inconsistency) => inconsistency.inconsistencyId),
                      );
                  }}
                >
                  <Icon className="lucide--alert-triangle h-3 w-3 text-red-500" />
                  Disputed
                </Button>
              </Chip>
            ) : (
              <></>
            )}

            {isFullFact && fact.issues && fact.issues.length > 0 && <ThemesTooltip issues={fact.issues} />}
          </div>
        </div>

        <div className="flex items-start justify-between gap-2">
          <div className={`line-clamp-1 w-5/6 pt-1 text-ellipsis text-gray-900`}>
            {open ? (
              <EditableField value={factText} onSave={updateFactDescription} isLoading={isUpdatingDescription} />
            ) : (
              factText
            )}
          </div>

          <div className="mt-1.5 -mb-0.75 flex items-center gap-2">
            {!hideRelevanceIndicator && (
              <div>
                <RelevanceIndicator relevance={relevance} />
              </div>
            )}

            {/* Show verify button for chronology facts */}
            {!isFullFact &&
              verification &&
              (verification.verified ? (
                <Popover>
                  <PopoverTrigger
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-0.75 rounded border border-green-500 bg-green-100 px-1 py-0.5 text-xs text-green-900 duration-150 hover:border-green-600"
                  >
                    <Icon className="lucide--shield-check -ml-0.25 h-4 w-4 text-green-600" />
                    <span className="-mb-[1px]">Verified</span>
                  </PopoverTrigger>

                  <PopoverContent side="left" className="flex w-auto items-center gap-2 p-3 text-xs">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        verification.handleToggleVerify(false);
                      }}
                      className="inline-flex items-center gap-0.75 rounded border border-red-500 bg-red-50 px-1 py-0.5 text-xs text-red-700 duration-150 hover:border-red-600"
                    >
                      <Icon className="lucide--shield-close h-4 w-4 text-red-600" />
                      <span className="-mb-[1px]">Unverify</span>
                    </button>
                    <span className="text-gray-600">
                      Verified by <strong>{verification.email}</strong> on{' '}
                      {moment(verification.date).format('MMM DD, YYYY')}.
                    </span>
                  </PopoverContent>
                </Popover>
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    trackCustomEvent(TrackingEvents.VerifyChronologyFact, {
                      factId: fact.id,
                      chronologyId: fact.chronologyId,
                    });
                    verification.handleToggleVerify(true);
                  }}
                  className="inline-flex items-center gap-0.75 rounded border border-gray-800 bg-gray-700 px-1.5 py-0.75 text-xs text-white duration-150 hover:border-gray-400 hover:bg-gray-600"
                >
                  <span className="-mb-[1px]">Verify</span>
                </button>
              ))}
          </div>

          {reference && (
            <p
              className={`absolute top-0 right-0 flex h-5 w-5 items-center justify-center rounded-bl-md border-b border-l bg-gray-50 text-xs text-blue-600`}
            >
              {reference}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
